/**
 * Roster (Participants) — the real Screen B, retrofitted into the locked shell. It renders its own
 * PageHeader (breadcrumb skyCare > Participants, single H1) + an interactive SkyDataGrid bound to
 * GET /api/v1/participants. All interactivity is SERVER-SIDE so pagination stays correct:
 *   - name search (debounced) -> ?q=    (op_dim_member.first_name / last_name, ILIKE, parameterized)
 *   - column sort             -> ?sort=&dir=  (whitelisted op_dim_member columns)
 *   - row drill               -> /member/:enterprise_member_key (REAL surrogate key)
 * DEFERRED facets (Center, Risk level) render as DISABLED chips naming their unblocking feed — visible
 * so the screen reads as a real PACE roster, never faked. Errors surface via SkyMessageBar (problem+json
 * title); rows/stack are never logged (PHI-safe).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import {
  SkyBadge,
  SkyDataGrid,
  SkyInput,
  SkyMessageBar,
  SkyText,
  type SkyDataGridColumn,
  type SortDirection,
} from "@skypointcloud/skypoint-ui";
import type { Participant, Provenance } from "@shared/contracts";
import { fetchEnvelope, ApiProblem } from "../lib/api";
import { ageLabel } from "../lib/age";
import { ProvenanceBadge } from "../provenance/ProvenanceBadge";
import { PageHeader } from "../shell/PageHeader";
import { PageFooter } from "../shell/PageFooter";

// B1: the non-cohort caveat must be PROMINENT (a persistent banner), never tooltip-only. op_dim_member
// (2299) is a GENERAL sandbox FHIR population, NOT the PACE-enrolled cohort (pace_roster = 3), and
// Coverage.type='PACE' is unverified — so the roster must never read as an authoritative PACE census.
const NON_COHORT_CAVEAT =
  "This roster is the general sandbox FHIR population (2299), NOT the PACE-enrolled cohort. " +
  "PACE cohort identification (Coverage.type=PACE) is unverified.";

const PAGE_SIZE = 20;

/**
 * P2-4: a future filter facet whose backing feed has not landed yet. Rendered as a labelled, read-only
 * caption chip ("Awaits roster feed") instead of a permanently-disabled empty dropdown - a dead disabled
 * control reads as broken, whereas a caption chip honestly says the facet is coming with its feed.
 */
function FeedAwaitChip({ label, caption }: { label: string; caption: string }) {
  return (
    <div className="flex w-56 max-w-full flex-col gap-1">
      <SkyText size={200}>{label}</SkyText>
      <SkyBadge color="subtle" appearance="tint" size="medium">
        {caption}
      </SkyBadge>
    </div>
  );
}

// Columns the SERVER can sort (must match the route's sort enum). Used to mark grid headers sortable.
const SORTABLE = new Set(["last_name", "first_name", "date_of_birth", "chronic_condition_count"]);

const columns: SkyDataGridColumn<Participant>[] = [
  {
    key: "last_name",
    header: "Last name",
    width: "18%",
    sortable: true,
    dataMode: "server",
    // Navigate into Member-360 by the non-PHI surrogate key (never MRN/participant_id). SPA Link, no reload.
    renderCell: (_v, row) => (
      <Link to={`/member/${row.enterprise_member_key}`}>{row.last_name}</Link>
    ),
  },
  { key: "first_name", header: "First name", width: "18%", sortable: true, dataMode: "server" },
  {
    key: "date_of_birth",
    header: "Date of birth",
    width: "15%",
    sortable: true,
    dataMode: "server",
    // P1-3 geriatric anchor: show the derived age beside the raw DOB ("DOB (Age 78)"). The age is
    // GUARDED — an implausible/future/missing sandbox DOB reads "Age unknown", never a wrong number.
    renderCell: (_v, row) => (
      <span>
        {row.date_of_birth ?? "Not recorded"}{" "}
        <span className="text-neutral-500">({ageLabel(row.date_of_birth)})</span>
      </span>
    ),
  },
  { key: "gender", header: "Gender", width: "12%" },
  { key: "enrollment_status", header: "Status", width: "13%" },
  {
    key: "chronic_condition_count",
    header: "Chronic conditions",
    width: "24%",
    align: "right",
    sortable: true,
    dataMode: "server",
  },
];

export function Roster() {
  const [rows, setRows] = useState<Participant[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [provenance, setProvenance] = useState<Provenance | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search box: the raw input, debounced into `q` so each keystroke does not fire a request.
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  // Server sort state.
  const [sort, setSort] = useState<string | null>(null);
  const [dir, setDir] = useState<SortDirection | null>(null);

  // Debounce the search input -> q (250ms). Resetting page to 1 happens in the fetch effect's deps.
  useEffect(() => {
    const t = setTimeout(() => setQ(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Any change to the query shape (search / sort) returns to page 1 so the user never lands on an
  // out-of-range page of the new result set.
  useEffect(() => {
    setPage(1);
  }, [q, sort, dir]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
    if (q) params.set("q", q);
    if (sort) params.set("sort", sort);
    if (sort && dir) params.set("dir", dir);
    return params.toString();
  }, [page, q, sort, dir]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchEnvelope<Participant[]>(`/api/v1/participants?${queryString}`)
      .then(({ data, meta }) => {
        if (cancelled) return;
        // Drift guard: collapse a truthy non-array 200 body to [] so it never reaches the grid's .map().
        setRows(Array.isArray(data) ? data : []);
        if (meta?.total != null) setTotal(meta.total);
        setProvenance(meta?.provenance);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // Surface the problem title only — no PHI, no stack.
        setError(e instanceof ApiProblem ? e.problem.title : "Failed to load participants.");
        setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [queryString]);

  // Wire the grid's sort callback to the server params. A whitelisted column only (the grid passes the
  // column key); clearing the sort (direction null) returns to the server default (last_name asc).
  function onSortChange(columnKey: string | null, direction: SortDirection | null) {
    if (!columnKey || !direction || !SORTABLE.has(columnKey)) {
      setSort(null);
      setDir(null);
      return;
    }
    setSort(columnKey);
    setDir(direction);
  }

  return (
    <>
      <PageHeader
        crumbs={[{ key: "home", label: "skyCare" }, { key: "participants", label: "Participants" }]}
        title="Participants"
        description="Operational roster read live from the skyCare Sandbox operation database."
      />
      <section className="mt-6 flex flex-1 flex-col gap-3">
        {/* P1-2: persistent structural non-cohort caveat (informational, not an elevated safety alert) -
            the roster is NOT the PACE cohort. */}
        <SkyMessageBar
          intent="info"
          title="General sandbox population, not the PACE cohort"
          message={NON_COHORT_CAVEAT}
        />

        {/* Filter toolbar: REAL name search + DEFERRED facet chips (disabled, feed-named — never faked). */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-72 max-w-full">
            <SkyInput
              label="Search by name"
              placeholder="Last or first name"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              clearable
              onClear={() => setSearchInput("")}
              contentBefore={<Search size={16} aria-hidden />}
            />
          </div>
          <FeedAwaitChip label="Center" caption="Awaits roster feed" />
          <FeedAwaitChip label="Risk level" caption="Awaits RAF / ML feed" />
          <div className="ml-auto flex items-center gap-2">
            {/* M1: parity with every other screen — the roster carries its own provenance badge. */}
            <SkyText size={200}>Provenance</SkyText>
            <ProvenanceBadge meta={provenance} />
          </div>
        </div>

        {error && <SkyMessageBar intent="error" title="Could not load participants" message={error} />}
        <SkyDataGrid<Participant>
          data={rows}
          columns={columns}
          getRowId={(r) => r.enterprise_member_key}
          onSortChange={onSortChange}
          pagination
          page={page}
          pageSize={PAGE_SIZE}
          totalItems={total}
          onPageChange={setPage}
          loading={loading}
          emptyMessage={q ? `No participants match "${q}"` : "No participants found"}
        />
      </section>
      <PageFooter />
    </>
  );
}
